import { useCallback, useMemo, useState } from 'react';

import { getPhoneNumberCountry } from '@change-corgi/core/phone';
import { useI18n } from '@change-corgi/core-react/i18n';

import { useCountryCode } from '#src/app/shared/hooks/l10n';
import { isUserPhoneNumberValid } from '#src/app/shared/utils/phoneCollection';
import type { PhoneNumber } from '#src/app/shared/utils/phoneCollection';

import type { PhoneOptInEventHandlers, PhoneOptInValidationErrorType } from '../events';
import type { PhoneOptInResult } from '../types';
import { useConsent } from '../useConsent';
import { useSubmitPhoneOptIn } from '../useSubmitPhoneOptIn';
import type { PhoneOptInPendingAction } from '../useSubmitPhoneOptIn';

type UseSmsOptInFormParams = Readonly<{
	inputRef: React.RefObject<HTMLInputElement | null>;
	/**
	 * The number currently on the account. Must reflect a number saved earlier in the same session,
	 * not a page-load snapshot.
	 */
	phoneNumber: PhoneNumber;
	events: PhoneOptInEventHandlers;
	onComplete: (result: PhoneOptInResult) => void;
}>;

type PhoneInputError = Readonly<{
	type: PhoneOptInValidationErrorType;
	message: string;
}>;

/**
 * Validates the phone input, returning the applicable error (if any) for an invalid number or an
 * unsupported country.
 *
 * SMS delivery only supports US and Canadian numbers, so an otherwise valid number from anywhere
 * else is rejected here.
 */
function getPhoneInputError(
	inputValue: string,
	/**
	 * Whether an empty field is itself an error. True only when the primary is an affirmative CTA:
	 * with a number on file the button says "opt me in", so clearing the field and pressing it asks
	 * to be texted at no number. Reporting that as an invalid entry keeps it from reaching the
	 * empty-field path, which would persist the opt-out the starter did not ask for. With no number
	 * on file the primary is "Continue" and an empty field is a refusal, as agreed.
	 */
	isRequired: boolean,
	translate: ReturnType<typeof useI18n>['translate'],
): PhoneInputError | undefined {
	const invalidNumberMessage = isRequired
		? translate('corgi.petition.starter_onboarding.phone_collection_step_sms.country_restriction_error')
		: translate('corgi.petition.starter_onboarding.phone_collection_step_sms.input_error');

	if (!inputValue) {
		if (!isRequired) return undefined;
		return { type: 'invalid_number', message: invalidNumberMessage };
	}

	const country = getPhoneNumberCountry(inputValue);
	// A country that can't be determined (e.g. isPossiblePhoneNumber but not isValidPhoneNumber)
	// means the number itself isn't valid, not that it's from an unsupported country.
	if (!isUserPhoneNumberValid(inputValue) || !country) {
		return { type: 'invalid_number', message: invalidNumberMessage };
	}
	if (country !== 'US' && country !== 'CA') {
		return {
			type: 'unsupported_country',
			message: translate('corgi.petition.starter_onboarding.phone_collection_step_sms.country_restriction_error'),
		};
	}
	return undefined;
}

export type UseSmsOptInFormReturn = ModelHookResult<
	{
		hasPhone: boolean;
		inputError: string | undefined;
		inputValue: string;
		countryCode: string;
		isSubmitting: boolean;
		/** Which ask is in flight, so the surface spins the control the starter pressed. */
		pendingAction: PhoneOptInPendingAction | null;
	},
	{
		handleOnChange: (value: string | undefined) => void;
		onSubmit: () => Promise<void>;
		onDecline: () => Promise<void>;
	}
>;

export function useSmsOptInForm({
	inputRef,
	phoneNumber,
	events,
	onComplete,
}: UseSmsOptInFormParams): UseSmsOptInFormReturn {
	const countryCode = useCountryCode();
	const { translate } = useI18n();

	const hasPhone = useMemo(() => isUserPhoneNumberValid(phoneNumber), [phoneNumber]);

	const initialPhone = isUserPhoneNumberValid(phoneNumber) ? (phoneNumber ?? '') : '';
	const [inputValue, setInputValue] = useState(initialPhone);
	const [inputError, setInputError] = useState<string>();
	const [isInputDirty, setIsInputDirty] = useState(false);

	const consent = useConsent('sms');
	const { isSubmitting, pendingAction, submit, decline } = useSubmitPhoneOptIn({
		screen: 'sms',
		consent,
		consentTrackingKey: 'starter_sms_petition_updates_consent',
		events,
		onComplete,
	});

	const handleOnChange = useCallback(
		(value: string | undefined) => {
			setInputError(undefined);
			setInputValue(value ?? '');
			if (!isInputDirty) {
				events.onPhoneEntry('sms');
				setIsInputDirty(true);
			}
		},
		[isInputDirty, events],
	);

	// `accepted` distinguishes opting in from refusing, so it is only meaningful when a number was
	// already on the account — with no number there is nothing to accept yet.
	const onSubmit = useCallback(async () => {
		const phoneInputError = getPhoneInputError(inputValue, hasPhone, translate);
		if (phoneInputError) {
			setInputError(phoneInputError.message);
			events.onValidationError('sms', phoneInputError.type);
			inputRef.current?.focus();
			return;
		}
		setInputError(undefined);

		const addedPhone = !!inputValue && isInputDirty && inputValue !== phoneNumber;

		await submit(inputValue || undefined, phoneNumber, {
			has_phone: !!phoneNumber,
			added_phone: addedPhone,
			...(hasPhone ? { accepted: true } : {}),
		});
	}, [inputValue, submit, translate, phoneNumber, isInputDirty, events, inputRef, hasPhone]);

	// Declining saves no phone number, whatever has been typed into the field: it means "no
	// consent", not "also update my number".
	const onDecline = useCallback(async () => {
		await decline(phoneNumber, { has_phone: true, added_phone: false, accepted: false });
	}, [decline, phoneNumber]);

	return {
		data: {
			hasPhone,
			inputError,
			inputValue,
			countryCode,
			isSubmitting,
			pendingAction,
		},
		actions: {
			handleOnChange,
			onSubmit,
			onDecline,
		},
	};
}
